import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid } from 'recharts';
import { 
    CalendarDays, TrendingUp, TrendingDown, Wallet, Users, MapPin, 
    X, Search, Receipt, ChevronRight, CheckCircle2, ChevronDown,
    Eye, Activity, ShieldCheck, Sun, Moon
} from 'lucide-react';

const MONTHS = ['','Jan','Feb','Mar','Apr','Mei','Jun','Jul','Agt','Sep','Okt','Nov','Des'];
const MONTHS_FULL = ['','Januari','Februari','Maret','April','Mei','Juni','Juli','Agustus','September','Oktober','November','Desember'];
const fmt = (n) => `Rp ${Number(n||0).toLocaleString('id-ID')}`;
const fmtNum = (n) => Number(n||0).toLocaleString('id-ID');

const CATEGORY_COLORS = {
    'Infrastruktur': 'bg-blue-50 text-blue-700 border-blue-200',
    'Kebersihan': 'bg-emerald-50 text-emerald-700 border-emerald-200',
    'Keamanan': 'bg-indigo-50 text-indigo-700 border-indigo-200',
    'Administrasi': 'bg-purple-50 text-purple-700 border-purple-200',
    'Perlengkapan': 'bg-amber-50 text-amber-700 border-amber-200',
    'Kegiatan': 'bg-rose-50 text-rose-700 border-rose-200',
    'Darurat': 'bg-red-50 text-red-700 border-red-200',
    'Lainnya': 'bg-slate-50 text-slate-700 border-slate-200',
};

const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
        <div className="bg-white border border-slate-200 rounded-xl shadow-lg p-2.5 text-xs">
            <p className="font-bold text-slate-700 mb-1">{MONTHS_FULL[label]}</p>
            {payload.map((p, i) => (
                <p key={i} style={{ color: p.color }} className="font-medium">
                    {p.name}: {fmt(p.value)}
                </p>
            ))}
        </div>
    );
};

export default function PublicDashboardPage() {
    const [activeYear, setActiveYear] = useState(2026);
    const [darkMode, setDarkMode] = useState(() => {
        return localStorage.getItem('rt6_theme') === 'dark';
    });

    useEffect(() => {
        if (darkMode) {
            document.documentElement.classList.add('dark');
            localStorage.setItem('rt6_theme', 'dark');
        } else {
            document.documentElement.classList.remove('dark');
            localStorage.setItem('rt6_theme', 'light');
        }
    }, [darkMode]);
    const [yearDropdownOpen, setYearDropdownOpen] = useState(false);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    // Modal state for viewing all monthly expenses
    const [showExpenseModal, setShowExpenseModal] = useState(false);
    const [expenses, setExpenses] = useState([]);
    const [loadingExpenses, setLoadingExpenses] = useState(false);
    const [selectedYear, setSelectedYear] = useState(2026);
    const [selectedMonth, setSelectedMonth] = useState('all');
    const [searchQuery, setSearchQuery] = useState('');

    // Load stats for active year
    const fetchStats = (yr) => {
        setLoading(true);
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/public/stats?year=${yr}`)
            .then(r => setData(r.data))
            .catch(() => {})
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        fetchStats(activeYear);
    }, [activeYear]);

    // Fetch all approved expenses when modal opens or year changes
    const fetchExpenses = (year) => {
        setLoadingExpenses(true);
        const url = `${process.env.REACT_APP_BACKEND_URL}/api/public/expenses?year=${year}`;
        axios.get(url)
            .then(r => {
                setExpenses(r.data || []);
            })
            .catch(err => {
                console.error("Gagal memuat pengeluaran:", err);
            })
            .finally(() => setLoadingExpenses(false));
    };

    const handleOpenExpenseModal = (month = 'all') => {
        setSelectedYear(activeYear);
        setSelectedMonth(month);
        setShowExpenseModal(true);
        fetchExpenses(activeYear);
    };

    const handleModalYearChange = (year) => {
        setSelectedYear(year);
        fetchExpenses(year);
    };

    // Filtered expenses based on month and search
    const filteredExpenses = useMemo(() => {
        return expenses.filter(item => {
            const matchMonth = selectedMonth === 'all' || item.month === Number(selectedMonth);
            const q = searchQuery.toLowerCase();
            const matchSearch = !searchQuery || 
                (item.description && item.description.toLowerCase().includes(q)) ||
                (item.category && item.category.toLowerCase().includes(q)) ||
                (item.notes && item.notes.toLowerCase().includes(q));
            return matchMonth && matchSearch;
        });
    }, [expenses, selectedMonth, searchQuery]);

    // Group expenses by month
    const groupedExpenses = useMemo(() => {
        const groups = {};
        filteredExpenses.forEach(exp => {
            const m = exp.month;
            if (!groups[m]) groups[m] = [];
            groups[m].push(exp);
        });
        return groups;
    }, [filteredExpenses]);

    const totalFilteredAmount = useMemo(() => {
        return filteredExpenses.reduce((acc, cur) => acc + (cur.amount || 0), 0);
    }, [filteredExpenses]);

    if (loading && !data) {
        return (
            <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
                <div className="text-center">
                    <div className="w-10 h-10 border-4 border-[#065f46] border-t-transparent rounded-full animate-spin mx-auto mb-3" />
                    <p className="text-slate-600 text-xs sm:text-sm font-medium">Memuat data transparansi warga...</p>
                </div>
            </div>
        );
    }

    const regional = data?.regional || {};
    const rtLabel = regional.rt_number ? `RT ${regional.rt_number} / RW ${regional.rw_number}` : 'RT 06 / RW 01 Menanggal';
    const locLabel = [regional.kelurahan, regional.kecamatan, regional.kota].filter(Boolean).join(', ') || 'Menanggal, Gayungan, Surabaya';

    const chartData = (data?.trend || []).map(d => ({
        month: d.month,
        Iuran: d.income,
        Pengeluaran: d.expense,
    }));

    const visitors = data?.visitors || { today: 0, this_month: 0, this_year: 0, total: 0 };

    return (
        <div className="min-h-screen pb-6 flex flex-col" style={{ background: 'hsl(210 40% 98%)' }} data-testid="public-dashboard">
            {/* Top bar (Mobile & PWA optimized) */}
            <header className="bg-white/95 backdrop-blur-md border-b border-slate-200 px-3 sm:px-6 py-2.5 sm:py-3 sticky top-0 z-40 shadow-xs">
                <div className="max-w-4xl mx-auto flex items-center justify-between gap-2">
                    {/* Brand / Logo */}
                    <div className="flex items-center gap-2 min-w-0">
                        <img 
                            src="/logo_surabaya.svg" 
                            alt="Logo Pemkot Surabaya" 
                            className="w-7 h-7 sm:w-8 sm:h-8 object-contain flex-shrink-0" 
                        />
                        <div className="min-w-0">
                            <h1 className="font-extrabold text-slate-800 dark:text-slate-100 text-xs sm:text-sm truncate leading-tight">
                                RT 06 / RW 01
                            </h1>
                            <p className="text-[10px] text-slate-400 dark:text-slate-400 font-medium truncate leading-none mt-0.5">
                                Menanggal, Gayungan
                            </p>
                        </div>
                    </div>

                    {/* Right actions: Compact Year Dropdown + Expense Modal Button + Home */}
                    <div className="flex items-center gap-1.5 sm:gap-2.5 flex-shrink-0">
                        {/* Compact Year Selector Dropdown */}
                        <div className="relative">
                            <button
                                onClick={() => setYearDropdownOpen(!yearDropdownOpen)}
                                className="inline-flex items-center gap-1 sm:gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-bold bg-slate-50 hover:bg-slate-100 text-slate-800 border border-slate-200 transition-all shadow-2xs active:scale-95"
                                aria-label="Pilih Tahun Transparansi"
                            >
                                <CalendarDays className="w-3.5 h-3.5 text-[#065f46]" />
                                <span className="font-extrabold text-[11px] sm:text-xs">{activeYear}</span>
                                <ChevronDown className={`w-3.5 h-3.5 text-slate-400 transition-transform ${yearDropdownOpen ? 'rotate-180 text-slate-700' : ''}`} />
                            </button>

                            {yearDropdownOpen && (
                                <>
                                    <div 
                                        className="fixed inset-0 z-40" 
                                        onClick={() => setYearDropdownOpen(false)} 
                                    />
                                    <div className="absolute right-0 mt-1.5 w-36 bg-white rounded-xl shadow-xl border border-slate-200 py-1.5 z-50 animate-in fade-in zoom-in-95 duration-100">
                                        <p className="px-3 py-1 text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                                            Pilih Tahun
                                        </p>
                                        {[2026, 2025, 2024].map(yr => (
                                            <button
                                                key={yr}
                                                onClick={() => {
                                                    setActiveYear(yr);
                                                    setYearDropdownOpen(false);
                                                }}
                                                className={`w-full text-left px-3 py-2 text-xs font-bold flex items-center justify-between transition-colors ${
                                                    activeYear === yr
                                                        ? 'bg-emerald-50 text-[#065f46]'
                                                        : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                                                }`}
                                            >
                                                <span>Tahun {yr}</span>
                                                {activeYear === yr && (
                                                    <CheckCircle2 className="w-3.5 h-3.5 text-[#065f46]" />
                                                )}
                                            </button>
                                        ))}
                                    </div>
                                </>
                            )}
                        </div>

                        {/* Rincian Belanja Button */}
                        <button 
                            onClick={() => handleOpenExpenseModal('all')}
                            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-bold bg-emerald-50 text-[#065f46] border border-emerald-200 hover:bg-emerald-100 active:scale-95 transition-all"
                            title="Buka rincian seluruh belanja kas RT"
                        >
                            <Receipt className="w-3.5 h-3.5 text-[#065f46]" />
                            <span className="hidden sm:inline">Rincian Belanja</span>
                        </button>

                        {/* Dark/Light Mode Toggle */}
                        <button
                            onClick={() => setDarkMode(!darkMode)}
                            aria-label="Toggle Dark/Light Mode"
                            className="inline-flex items-center gap-1 p-1.5 sm:px-2 sm:py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 transition-all text-xs font-semibold shadow-2xs"
                        >
                            {darkMode ? (
                                <Sun className="w-3.5 h-3.5 text-amber-400" />
                            ) : (
                                <Moon className="w-3.5 h-3.5 text-slate-600" />
                            )}
                        </button>

                        <a 
                            href="/" 
                            className="text-xs font-bold text-[#065f46] hover:text-[#064e3b] px-2 py-1.5 rounded-lg hover:bg-emerald-50/60 transition-colors"
                        >
                            ← <span className="hidden xs:inline">Beranda</span>
                        </a>
                    </div>
                </div>
            </header>

            {/* Main Content Area */}
            <main className="max-w-4xl mx-auto px-3 sm:px-4 py-4 sm:py-6 space-y-4 sm:space-y-6 flex-1 w-full">
                {/* Hero Identity Banner */}
                <div 
                    className="rounded-2xl overflow-hidden shadow-sm"
                    style={{ background: 'linear-gradient(135deg, #064e3b 0%, #065f46 55%, #0F766E 100%)' }}
                >
                    <div className="p-5 sm:p-7 flex flex-col sm:flex-row items-center sm:items-start text-center sm:text-left gap-4 sm:gap-6">
                        <img 
                            src="/logo_surabaya.svg" 
                            alt="Logo RT 06 Surabaya" 
                            className="w-16 h-16 sm:w-20 sm:h-20 object-contain bg-white/10 rounded-2xl p-2 flex-shrink-0 shadow-inner" 
                        />
                        <div className="text-white flex-1 min-w-0">
                            <p className="text-[11px] text-white/75 tracking-wider uppercase font-bold mb-1">
                                Portal Transparansi Keuangan Warga
                            </p>
                            <h2 className="text-2xl sm:text-3xl font-extrabold leading-tight mb-1.5" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                                RT 06 / RW 01
                            </h2>
                            <p className="text-white/80 flex items-center justify-center sm:justify-start gap-1.5 text-xs sm:text-sm font-medium">
                                <MapPin className="w-3.5 h-3.5 flex-shrink-0" /> 
                                <span className="truncate">Menanggal, Gayungan</span>
                            </p>
                            <div className="mt-3.5 flex flex-wrap items-center justify-center sm:justify-start gap-1.5 sm:gap-2">
                                <span className="bg-white/20 backdrop-blur-xs text-white text-[11px] px-2.5 py-0.5 rounded-full font-bold border border-white/20">
                                    Tahun {activeYear} &bull; {MONTHS_FULL[data?.current_month]}
                                </span>
                                <span className="bg-emerald-950/40 text-emerald-100 text-[11px] px-2.5 py-0.5 rounded-full font-semibold border border-emerald-400/30">
                                    72 KK Tetap
                                </span>
                                <span className="bg-emerald-400/20 text-emerald-200 text-[11px] px-2.5 py-0.5 rounded-full font-semibold border border-emerald-400/30">
                                    Partisipasi Aktif 100%
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Stats Grid (Responsive 2 cols on mobile, 4 on desktop) */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                    {/* Card 1: Pemasukan Bulan Ini */}
                    <div className="bg-white rounded-xl border border-slate-200 p-3.5 sm:p-5 shadow-2xs">
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-[10px] sm:text-xs text-slate-500 uppercase tracking-wider font-bold">
                                Pemasukan
                            </span>
                            <div className="w-7 h-7 sm:w-9 sm:h-9 rounded-lg flex items-center justify-center bg-green-50 text-green-600">
                                <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5" />
                            </div>
                        </div>
                        <p className="text-base sm:text-xl font-extrabold text-slate-900 leading-tight truncate" style={{ fontFamily: 'Plus Jakarta Sans' }}>
                            {fmt(data?.iuran_total)}
                        </p>
                        <p className="text-[10px] sm:text-xs text-emerald-600 mt-1 font-semibold truncate">
                            {data?.paid_count || 0} KK terdata
                        </p>
                    </div>

                    {/* Card 2: Pengeluaran (Clickable for Detail) */}
                    <div 
                        onClick={() => handleOpenExpenseModal(data?.current_month || 'all')}
                        className="bg-white rounded-xl border-2 border-orange-200 p-3.5 sm:p-5 shadow-2xs hover:shadow-md transition-all cursor-pointer group bg-gradient-to-br from-white to-orange-50/30 active:scale-98"
                    >
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-[10px] sm:text-xs text-slate-600 uppercase tracking-wider font-bold">
                                Pengeluaran
                            </span>
                            <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-orange-100 text-orange-700 group-hover:bg-orange-200 transition-colors">
                                Rincian ↗
                            </span>
                        </div>
                        <p className="text-base sm:text-xl font-extrabold text-orange-600 leading-tight truncate" style={{ fontFamily: 'Plus Jakarta Sans' }}>
                            {fmt(data?.expense_total)}
                        </p>
                        <p className="text-[10px] sm:text-xs text-slate-500 mt-1 truncate">
                            Klik lihat nota belanja
                        </p>
                    </div>

                    {/* Card 3: Sisa Saldo Kas Akhir */}
                    <div className="bg-white rounded-xl border-2 border-emerald-300 p-3.5 sm:p-5 shadow-2xs bg-emerald-50/20 col-span-2 sm:col-span-1">
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-[10px] sm:text-xs text-slate-600 uppercase tracking-wider font-bold">
                                Sisa Saldo Kas Akhir
                            </span>
                            <div className="w-7 h-7 sm:w-9 sm:h-9 rounded-lg flex items-center justify-center bg-emerald-100 text-emerald-800">
                                <Wallet className="w-4 h-4 sm:w-5 sm:h-5" />
                            </div>
                        </div>
                        <p className="text-lg sm:text-xl font-black text-emerald-800 leading-tight truncate" style={{ fontFamily: 'Plus Jakarta Sans' }}>
                            {fmt(data?.ending_balance ?? data?.balance)}
                        </p>
                        <p className="text-[10px] sm:text-xs text-emerald-700 font-semibold mt-1">
                            Kas Nyata Berjalan
                        </p>
                    </div>

                    {/* Card 4: Status Operasional */}
                    <div className="bg-white rounded-xl border border-slate-200 p-3.5 sm:p-5 shadow-2xs col-span-2 sm:col-span-1">
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-[10px] sm:text-xs text-slate-500 uppercase tracking-wider font-bold">
                                Kas Bersih Bulan Ini
                            </span>
                            <div className="w-7 h-7 sm:w-9 sm:h-9 rounded-lg flex items-center justify-center bg-blue-50 text-blue-600">
                                <Activity className="w-4 h-4 sm:w-5 sm:h-5" />
                            </div>
                        </div>
                        <p className={`text-base sm:text-xl font-extrabold leading-tight truncate ${(data?.net_monthly || 0) >= 0 ? 'text-blue-700' : 'text-rose-600'}`} style={{ fontFamily: 'Plus Jakarta Sans' }}>
                            {(data?.net_monthly || 0) >= 0 ? `+${fmt(data?.net_monthly)}` : fmt(data?.net_monthly)}
                        </p>
                        <p className="text-[10px] sm:text-xs text-slate-400 mt-1">
                            {(data?.net_monthly || 0) >= 0 ? 'Surplus Operasional' : 'Defisit Operasional'}
                        </p>
                    </div>
                </div>

                {/* Banner CTA Transparansi Pengeluaran */}
                <div 
                    onClick={() => handleOpenExpenseModal('all')}
                    className="rounded-xl border border-emerald-200 bg-emerald-50/70 p-3.5 sm:p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 cursor-pointer hover:bg-emerald-100/70 transition-all active:scale-99"
                >
                    <div className="flex items-center gap-3">
                        <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-[#065f46] text-white flex items-center justify-center flex-shrink-0 shadow-xs">
                            <Receipt className="w-4 h-4 sm:w-5 sm:h-5" />
                        </div>
                        <div>
                            <h3 className="font-bold text-slate-900 text-xs sm:text-sm">
                                Buku Transparansi Pengeluaran Kas RT 06
                            </h3>
                            <p className="text-[11px] sm:text-xs text-slate-600">
                                Transparansi nota fisik, rekening listrik PJU, dan honor kebersihan
                            </p>
                        </div>
                    </div>
                    <button className="w-full sm:w-auto flex items-center justify-center gap-1 text-xs font-bold px-3 py-2 rounded-lg bg-[#065f46] text-white hover:bg-[#064e3b] transition-colors whitespace-nowrap shadow-xs">
                        Lihat Seluruh Pengeluaran <ChevronRight className="w-4 h-4" />
                    </button>
                </div>

                {/* Chart Arus Kas Bulanan */}
                {chartData.length > 0 && (
                    <div className="bg-white rounded-xl border border-slate-200 p-4 sm:p-6 shadow-2xs">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-3 sm:mb-4 gap-1.5">
                            <div>
                                <h3 className="font-bold text-slate-800 text-sm sm:text-base" style={{ fontFamily: 'Plus Jakarta Sans' }}>
                                    Grafik Arus Kas Bulanan Tahun {activeYear}
                                </h3>
                                <p className="text-[11px] sm:text-xs text-slate-400">Pemasukan iuran vs pengeluaran kas RT 06 per bulan</p>
                            </div>
                            <button
                                onClick={() => handleOpenExpenseModal('all')}
                                className="text-xs font-semibold text-[#065f46] hover:underline flex items-center gap-1 self-start sm:self-auto"
                            >
                                <Receipt className="w-3.5 h-3.5" /> Buka Rincian Biaya
                            </button>
                        </div>
                        <div className="w-full h-52 sm:h-60">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={chartData} margin={{ top: 5, right: 5, left: -15, bottom: 0 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                                    <XAxis dataKey="month" tickFormatter={v => MONTHS[v]} tick={{ fontSize: 10, fill: '#64748B' }} axisLine={false} tickLine={false} />
                                    <YAxis tickFormatter={v => v >= 1000000 ? `${(v/1000000).toFixed(1)}jt` : v >= 1000 ? `${(v/1000).toFixed(0)}rb` : v}
                                        tick={{ fontSize: 9, fill: '#94A3B8' }} axisLine={false} tickLine={false} width={40} />
                                    <Tooltip content={<CustomTooltip />} />
                                    <Legend formatter={(v) => <span style={{ fontSize: 11, color: '#475569' }}>{v}</span>} />
                                    <Bar dataKey="Iuran" fill="#065f46" radius={[4, 4, 0, 0]} maxBarSize={28} />
                                    <Bar 
                                        dataKey="Pengeluaran" 
                                        fill="#F59E0B" 
                                        radius={[4, 4, 0, 0]} 
                                        maxBarSize={28}
                                        cursor="pointer"
                                        onClick={(entry) => handleOpenExpenseModal(entry.month)} 
                                    />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                )}

                {/* Tabel Rekapitulasi Aliran Saldo Bulanan */}
                <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-2xs">
                    <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-slate-200 bg-slate-50/70 flex items-center justify-between">
                        <div>
                            <h3 className="font-bold text-slate-900 text-xs sm:text-sm" style={{ fontFamily: 'Plus Jakarta Sans' }}>
                                Rekapitulasi Kas Berjalan &mdash; Tahun {activeYear}
                            </h3>
                            <p className="text-[10px] sm:text-xs text-slate-500">
                                Saldo akhir tiap bulan bersambung otomatis ke saldo awal bulan berikutnya
                            </p>
                        </div>
                        <span className="text-[10px] sm:text-xs font-bold text-slate-600 bg-slate-200/70 px-2 py-0.5 rounded-md">
                            {data?.trend?.length || 0} Bulan
                        </span>
                    </div>

                    <div className="overflow-x-auto">
                        <table className="w-full text-left text-xs border-collapse min-w-[500px]">
                            <thead>
                                <tr className="border-b border-slate-200 bg-slate-100/60 text-slate-600 font-bold uppercase tracking-wider text-[10px]">
                                    <th className="py-2.5 px-3">Bulan</th>
                                    <th className="py-2.5 px-3 text-right">Saldo Kas Awal</th>
                                    <th className="py-2.5 px-3 text-right text-emerald-800">(+) Pemasukan</th>
                                    <th className="py-2.5 px-3 text-right text-orange-800">(-) Pengeluaran</th>
                                    <th className="py-2.5 px-3 text-right font-black text-slate-900">(=) Sisa Saldo Akhir</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {(data?.trend || []).map((row, idx) => (
                                    <tr key={idx} className="hover:bg-slate-50/80 transition-colors">
                                        <td className="py-2.5 px-3 font-bold text-slate-800">
                                            {MONTHS_FULL[row.month]} {row.year}
                                        </td>
                                        <td className="py-2.5 px-3 text-right text-slate-600">
                                            {fmt(row.opening_balance)}
                                        </td>
                                        <td className="py-2.5 px-3 text-right font-semibold text-emerald-700">
                                            +{fmt(row.income)}
                                        </td>
                                        <td className="py-2.5 px-3 text-right font-semibold text-orange-700">
                                            -{fmt(row.expense)}
                                        </td>
                                        <td className="py-2.5 px-3 text-right font-black text-emerald-800 bg-emerald-50/30">
                                            {fmt(row.ending_balance)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* ─── FOOTBAR: STATISTIK PENGUNJUNG SITUS (HARI, BULAN, TAHUN) ─── */}
                <footer className="bg-white rounded-2xl border border-slate-200 p-4 sm:p-5 shadow-2xs space-y-3.5">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between pb-3 border-b border-slate-100 gap-1.5">
                        <div className="flex items-center gap-2">
                            <div className="w-7 h-7 rounded-lg bg-emerald-100 text-[#065f46] flex items-center justify-center flex-shrink-0">
                                <Eye className="w-4 h-4" />
                            </div>
                            <div>
                                <h3 className="text-xs sm:text-sm font-bold text-slate-900 leading-tight">
                                    Statistik Kunjungan Dasbor Transparansi
                                </h3>
                                <p className="text-[10px] sm:text-xs text-slate-500">
                                    Pelacakan statistik pengunjung warga RT 06 RW 01 Menanggal
                                </p>
                            </div>
                        </div>
                        <div className="flex items-center gap-1.5 self-start sm:self-auto">
                            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
                            <span className="text-[11px] font-semibold text-emerald-700">Telemetri Aktif</span>
                        </div>
                    </div>

                    {/* 4 Cards Grid: Hari Ini | Bulan Ini | Tahun Ini | Total */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5 sm:gap-3">
                        {/* Hari ini */}
                        <div className="bg-slate-50 border border-slate-200/80 rounded-xl p-3 text-center sm:text-left">
                            <div className="flex items-center justify-center sm:justify-between mb-1">
                                <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                                    Hari Ini
                                </span>
                                <Users className="w-3.5 h-3.5 text-emerald-600 hidden sm:block" />
                            </div>
                            <p className="text-lg sm:text-xl font-extrabold text-slate-900 leading-none mb-1" style={{ fontFamily: 'Plus Jakarta Sans' }}>
                                {fmtNum(visitors.today)}
                            </p>
                            <p className="text-[10px] text-slate-400">Pengunjung unik</p>
                        </div>

                        {/* Bulan ini */}
                        <div className="bg-slate-50 border border-slate-200/80 rounded-xl p-3 text-center sm:text-left">
                            <div className="flex items-center justify-center sm:justify-between mb-1">
                                <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                                    Bulan Ini
                                </span>
                                <CalendarDays className="w-3.5 h-3.5 text-blue-600 hidden sm:block" />
                            </div>
                            <p className="text-lg sm:text-xl font-extrabold text-blue-900 leading-none mb-1" style={{ fontFamily: 'Plus Jakarta Sans' }}>
                                {fmtNum(visitors.this_month)}
                            </p>
                            <p className="text-[10px] text-slate-400">{MONTHS_FULL[data?.current_month]} {activeYear}</p>
                        </div>

                        {/* Tahun ini */}
                        <div className="bg-slate-50 border border-slate-200/80 rounded-xl p-3 text-center sm:text-left">
                            <div className="flex items-center justify-center sm:justify-between mb-1">
                                <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                                    Tahun {activeYear}
                                </span>
                                <TrendingUp className="w-3.5 h-3.5 text-amber-600 hidden sm:block" />
                            </div>
                            <p className="text-lg sm:text-xl font-extrabold text-amber-800 leading-none mb-1" style={{ fontFamily: 'Plus Jakarta Sans' }}>
                                {fmtNum(visitors.this_year)}
                            </p>
                            <p className="text-[10px] text-slate-400">Akumulasi tahunan</p>
                        </div>

                        {/* Total Kumulatif */}
                        <div className="bg-slate-50 border border-slate-200/80 rounded-xl p-3 text-center sm:text-left">
                            <div className="flex items-center justify-center sm:justify-between mb-1">
                                <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                                    Total Kunjungan
                                </span>
                                <Eye className="w-3.5 h-3.5 text-purple-600 hidden sm:block" />
                            </div>
                            <p className="text-lg sm:text-xl font-extrabold text-purple-900 leading-none mb-1" style={{ fontFamily: 'Plus Jakarta Sans' }}>
                                {fmtNum(visitors.total)}
                            </p>
                            <p className="text-[10px] text-slate-400">Semua Waktu</p>
                        </div>
                    </div>

                    <div className="pt-2 text-center border-t border-slate-100 flex flex-col sm:flex-row items-center justify-between text-[11px] text-slate-400 gap-1">
                        <span>&copy; {new Date().getFullYear()} Sistem Informasi RT 06 RW 01 Menanggal, Gayungan, Surabaya</span>
                        <span className="flex items-center gap-1 text-[#065f46] font-semibold">
                            <ShieldCheck className="w-3.5 h-3.5" /> Portal Transparansi Resmi Warga
                        </span>
                    </div>
                </footer>
            </main>

            {/* ─── MODAL LIHAT SEMUA PENGELUARAN TIAP BULAN (MOBILE & PWA READY) ─── */}
            {showExpenseModal && (
                <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-slate-900/60 backdrop-blur-xs animate-in fade-in duration-200">
                    <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl border border-slate-200 w-full max-w-4xl max-h-[92vh] sm:max-h-[90vh] flex flex-col overflow-hidden">
                        
                        {/* Header Modal */}
                        <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-slate-200 flex items-center justify-between bg-slate-50/80">
                            <div className="flex items-center gap-2.5 sm:gap-3">
                                <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl bg-orange-100 text-orange-600 flex items-center justify-center flex-shrink-0">
                                    <Receipt className="w-4 h-4 sm:w-5 sm:h-5" />
                                </div>
                                <div>
                                    <h3 className="text-sm sm:text-lg font-bold text-slate-900 leading-tight" style={{ fontFamily: 'Plus Jakarta Sans' }}>
                                        Daftar Rincian Pengeluaran RT 06
                                    </h3>
                                    <p className="text-[10px] sm:text-xs text-slate-500">
                                        Transparansi kuitansi belanja & listrik PJU
                                    </p>
                                </div>
                            </div>
                            <button
                                onClick={() => setShowExpenseModal(false)}
                                className="p-1.5 sm:p-2 text-slate-400 hover:text-slate-700 hover:bg-slate-200 rounded-lg transition-colors"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        {/* Filter Bar di dalam Modal */}
                        <div className="p-3 sm:p-4 border-b border-slate-200 bg-slate-50/40 flex flex-wrap gap-2.5 items-center justify-between">
                            {/* Filter Tahun */}
                            <div className="inline-flex rounded-lg border border-slate-200 p-0.5 bg-white shadow-2xs">
                                {[2026, 2025, 2024].map(yr => (
                                    <button
                                        key={yr}
                                        onClick={() => handleModalYearChange(yr)}
                                        className={`px-2.5 sm:px-3 py-1 rounded-md text-xs font-bold transition-all ${
                                            selectedYear === yr 
                                                ? 'bg-[#065f46] text-white shadow-xs' 
                                                : 'text-slate-600 hover:text-slate-900'
                                        }`}
                                    >
                                        {yr}
                                    </button>
                                ))}
                            </div>

                            {/* Search Box */}
                            <div className="relative flex-1 min-w-[160px] max-w-xs">
                                <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-2.5" />
                                <input
                                    type="text"
                                    placeholder="Cari nota, kategori..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="w-full pl-8 pr-3 py-1 text-xs rounded-lg border border-slate-200 bg-white focus:outline-hidden focus:ring-2 focus:ring-[#065f46]"
                                />
                                {searchQuery && (
                                    <button onClick={() => setSearchQuery('')} className="absolute right-2 top-2 text-slate-400 hover:text-slate-600">
                                        <X className="w-3 h-3" />
                                    </button>
                                )}
                            </div>

                            {/* Info Total Terfilter */}
                            <div className="text-right w-full sm:w-auto">
                                <span className="text-[10px] sm:text-[11px] text-slate-500">Total: </span>
                                <span className="text-xs sm:text-sm font-extrabold text-orange-600" style={{ fontFamily: 'Plus Jakarta Sans' }}>
                                    {fmt(totalFilteredAmount)}
                                </span>
                            </div>
                        </div>

                        {/* Filter Tab Bulan */}
                        <div className="px-3 sm:px-4 py-2 border-b border-slate-200 bg-white overflow-x-auto flex gap-1.5 items-center">
                            <button
                                onClick={() => setSelectedMonth('all')}
                                className={`px-2.5 py-1 rounded-full text-xs font-semibold whitespace-nowrap transition-colors ${
                                    selectedMonth === 'all'
                                        ? 'bg-[#065f46] text-white'
                                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                }`}
                            >
                                Semua Bulan
                            </button>
                            {(selectedYear === 2026 ? [1,2,3,4,5,6,7,8,9,10] : [1,2,3,4,5,6,7,8,9,10,11,12]).map(m => (
                                <button
                                    key={m}
                                    onClick={() => setSelectedMonth(m)}
                                    className={`px-2.5 py-1 rounded-full text-xs font-semibold whitespace-nowrap transition-colors ${
                                        selectedMonth === m
                                            ? 'bg-[#065f46] text-white'
                                            : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                    }`}
                                >
                                    {MONTHS[m]}
                                </button>
                            ))}
                        </div>

                        {/* Body Modal (Daftar Pengeluaran) */}
                        <div className="flex-1 overflow-y-auto p-3 sm:p-6 space-y-3 sm:space-y-4">
                            {loadingExpenses ? (
                                <div className="text-center py-12">
                                    <div className="w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-2" />
                                    <p className="text-xs text-slate-500">Memuat rincian transaksi pengeluaran...</p>
                                </div>
                            ) : filteredExpenses.length === 0 ? (
                                <div className="text-center py-10 bg-slate-50 rounded-xl border border-dashed border-slate-200">
                                    <Receipt className="w-8 h-8 text-slate-300 mx-auto mb-1.5" />
                                    <p className="text-xs sm:text-sm font-semibold text-slate-600">Tidak ada pengeluaran</p>
                                </div>
                            ) : (
                                Object.keys(groupedExpenses)
                                    .sort((a, b) => Number(b) - Number(a))
                                    .map(m => {
                                        const monthNum = Number(m);
                                        const list = groupedExpenses[m];
                                        const subtotal = list.reduce((acc, c) => acc + (c.amount || 0), 0);
                                        return (
                                            <div key={m} className="border border-slate-200 rounded-xl overflow-hidden bg-white shadow-2xs">
                                                <div className="bg-slate-50 px-3.5 py-2 border-b border-slate-200 flex items-center justify-between">
                                                    <div className="flex items-center gap-1.5">
                                                        <span className="w-2 h-2 rounded-full bg-orange-500" />
                                                        <span className="font-bold text-slate-800 text-xs sm:text-sm">
                                                            {MONTHS_FULL[monthNum]} {selectedYear}
                                                        </span>
                                                        <span className="text-[10px] text-slate-400">({list.length})</span>
                                                    </div>
                                                    <span className="text-xs font-bold text-orange-600">
                                                        {fmt(subtotal)}
                                                    </span>
                                                </div>
                                                <div className="divide-y divide-slate-100">
                                                    {list.map(item => (
                                                        <div key={item.id} className="p-3 sm:p-4 hover:bg-slate-50/60 transition-colors flex flex-col sm:flex-row sm:items-center justify-between gap-1.5 sm:gap-2">
                                                            <div className="space-y-0.5">
                                                                <div className="flex flex-wrap items-center gap-1.5">
                                                                    <span className={`text-[9px] sm:text-[10px] font-bold px-1.5 py-0.5 rounded-full border ${CATEGORY_COLORS[item.category] || CATEGORY_COLORS['Lainnya']}`}>
                                                                        {item.category}
                                                                    </span>
                                                                    <span className="text-xs font-bold text-slate-800">
                                                                        {item.description || item.title}
                                                                    </span>
                                                                </div>
                                                                <div className="flex flex-wrap items-center gap-2 text-[11px] text-slate-500">
                                                                    <span className="flex items-center gap-1">
                                                                        <CalendarDays className="w-3 h-3 text-slate-400" />
                                                                        {item.date || `${item.year}-${item.month}`}
                                                                    </span>
                                                                    {item.notes && (
                                                                        <span className="italic text-slate-400 truncate max-w-xs">
                                                                            &bull; {item.notes}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </div>
                                                            <div className="text-right flex-shrink-0 flex sm:flex-col items-center sm:items-end justify-between sm:justify-start">
                                                                <span className="text-xs sm:text-sm font-extrabold text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>
                                                                    {fmt(item.amount)}
                                                                </span>
                                                                <span className="text-[9px] text-emerald-600 font-semibold">
                                                                    ✓ Terbayar
                                                                </span>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        );
                                    })
                            )}
                        </div>

                        {/* Footer Modal */}
                        <div className="px-4 py-2.5 border-t border-slate-200 bg-slate-50 flex items-center justify-between text-xs text-slate-500">
                            <span className="text-[11px]">{filteredExpenses.length} transaksi</span>
                            <button
                                onClick={() => setShowExpenseModal(false)}
                                className="px-3 py-1 rounded-lg bg-slate-200 hover:bg-slate-300 text-slate-800 font-bold transition-colors text-xs"
                            >
                                Tutup
                            </button>
                        </div>

                    </div>
                </div>
            )}
        </div>
    );
}
